import { useMemo, useState } from 'react';
import { Search, Plus, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { useAssignableStudents, useOrgBatchManagement } from '@/lib/hooks/useOrg';

export function AddStudentsDialog({ batchId, open, onOpenChange }: { batchId: string; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { data, isLoading } = useAssignableStudents(batchId, open);
  const m = useOrgBatchManagement(batchId);
  const [q, setQ] = useState('');

  const list = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (data ?? []).filter((s) => !query || s.name.toLowerCase().includes(query) || s.email.toLowerCase().includes(query));
  }, [data, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Add students</DialogTitle></DialogHeader>
        <p className="-mt-1 text-sm text-[#6b7280]">Enroll existing organization members into this batch.</p>
        <div className="mt-2 flex h-10 items-center gap-2 rounded-xl border border-[#ececec] px-3">
          <Search className="h-4 w-4 text-[#9ca3af]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" className="w-full bg-transparent text-sm outline-none" />
        </div>
        <div className="mt-3 max-h-80 space-y-1 overflow-y-auto">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-[#9ca3af]">Loading…</p>
          ) : list.length === 0 ? (
            <p className="py-8 text-center text-sm text-[#9ca3af]">No members available to add.</p>
          ) : (
            list.map((s) => {
              const pending = m.enrollStudent.isPending && m.enrollStudent.variables?.userId === s.user_id;
              return (
                <div key={s.user_id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-[#fafafa]">
                  <OrgAvatar name={s.name} size={32} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[#111827]">{s.name}</p>
                    <p className="truncate text-xs text-[#9ca3af]">{s.email}</p>
                  </div>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => m.enrollStudent.mutate({ batchId, userId: s.user_id })}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#ececec] px-3 py-1.5 text-sm font-medium text-[#ff5a1f] hover:bg-[#fff1ec] disabled:opacity-50"
                  >
                    {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
                  </button>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
